import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LogoImage from '@/components/base/LogoImage';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t('nav_home'), href: '#inicio' },
    { label: t('nav_catalog'), href: '#catalogo' },
    { label: t('nav_about'), href: '#nosotros' },
    { label: t('nav_testimonials'), href: '#testimonios' },
    { label: t('nav_contact'), href: '#contacto' },
  ];

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'es' ? 'en' : 'es');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <LogoImage variant={scrolled ? 'dark' : 'light'} />

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`text-sm font-medium transition-colors hover:text-brand-gold ${
                  scrolled ? 'text-brand-dark' : 'text-white/90'
                }`}
              >
                {item.label}
              </a>
            ))}

            {/* Language Switcher */}
            <button
              onClick={toggleLang}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all cursor-pointer ${
                scrolled
                  ? 'border-brand-red/20 text-brand-dark hover:bg-brand-red/10'
                  : 'border-white/20 text-white/90 hover:bg-white/10'
              }`}
            >
              <i className="ri-global-line" />
              {i18n.language === 'es' ? 'EN' : 'ES'}
            </button>
          </div>

          {/* CTA */}
          <a
            href="#contacto"
            className="hidden md:inline-flex items-center px-5 py-2.5 bg-brand-gold text-brand-dark text-sm font-semibold rounded-full hover:bg-brand-gold/90 transition-colors whitespace-nowrap"
          >
            {t('nav_visit')}
          </a>

          {/* Mobile controls */}
          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={toggleLang}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition-all cursor-pointer ${
                scrolled
                  ? 'border-brand-red/20 text-brand-dark'
                  : 'border-white/20 text-white/90'
              }`}
            >
              <i className="ri-global-line" />
              {i18n.language === 'es' ? 'EN' : 'ES'}
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                scrolled ? 'text-brand-dark' : 'text-white'
              }`}
            >
              <i className={`ri-${isOpen ? 'close' : 'menu'}-line text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-lg border-t border-gray-100 shadow-xl">
          <div className="px-4 py-6 space-y-1 text-center">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setIsOpen(false)}
                className="block text-brand-dark text-base font-medium py-3 hover:text-brand-red transition-colors"
              >
                {item.label}
              </a>
            ))}
            <div className="pt-4">
              <a
                href="#contacto"
                onClick={() => setIsOpen(false)}
                className="inline-flex items-center justify-center px-6 py-3 bg-brand-gold text-brand-dark font-semibold rounded-full text-sm"
              >
                {t('nav_visit')}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}